import React from "react";
import {
  Box,
  Card,
  Typography,
  useTheme,
  Stack,
  Divider,
} from "@mui/material";
import { GridLegacy as Grid } from "@mui/material";
import rupeeIcon from "../../Assets/images/svg/Icons/coins-money-svgrepo-com.svg";
import paidInvoice from "../../Assets/images/svg/Icons/invoice-bill-svgrepo-com.svg";
import InvoiceRevenue from "../../Assets/images/svg/Icons/layer1 (1).svg";
import upIcon from "../../Assets/images/svg/Icons/layer1.svg";
import downIcon from "../../Assets/images/svg/Icons/layer1 (2).svg";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";

const Dashboard: React.FC = () => {
  const theme = useTheme();

  // Sample bar chart data
  const data = [
    { month: "Feb", lastYear: 4000, currentYear: 16000 },
    { month: "Mar", lastYear: 7000, currentYear: 22000 },
    { month: "Apr", lastYear: 12000, currentYear: 11000 },
    { month: "May", lastYear: 3000, currentYear: 16000 },
    { month: "Jun", lastYear: 2000, currentYear: 22000 },
    { month: "Jul", lastYear: 5000, currentYear: 16000 },
    { month: "Aug", lastYear: 10000, currentYear: 21000 },
    { month: "Sep", lastYear: 3000, currentYear: 17000 },
    { month: "Oct", lastYear: 4000, currentYear: 10000 },
    { month: "Nov", lastYear: 2000, currentYear: 23000 },
    { month: "Dec", lastYear: 7000, currentYear: 15000 },
    { month: "Jan", lastYear: 3000, currentYear: 17000 },
  ];

  // Pie chart data (Bookings)
  const pieData = [
    { name: "Completed", value: 8, color: "#22C55E" },
    { name: "On Going", value: 25, color: "#F97316" },
    { name: "Upcoming", value: 22, color: "#3B82F6" },
  ];

  const totalBookings = pieData.reduce((a, b) => a + b.value, 0);

  return (
    <AuthorizedLayout>
      <Box sx={{ p: 3, bgcolor: "#F6F6F6" }}>
        {/* Top Cards */}
    <Grid container spacing={2} mb={3}>
  {[
    {
      title: "Revenue",
      color: "#24BC94",
      value: "₹15,000",
      date: "Feb 2024",
      icon: rupeeIcon,
      arrow: upIcon,
    },
    {
      title: "Expense",
      color: "#FF7D73",
      value: "₹5,000",
      date: "Feb 2024",
      icon: rupeeIcon,
      arrow: downIcon,
    },
    {
      title: "Paid Invoice",
      color: "#3B82F6",
      value: "₹11,265",
      date: "Last 30 Days",
      icon: paidInvoice,
      arrow: InvoiceRevenue,
    },
    {
      title: "Open Invoice",
      color: "#F97316",
      value: "₹21,052",
      date: "Last 30 Days",
      icon: paidInvoice,
      arrow: InvoiceRevenue,
    },
  ].map((item, index) => (
    <Grid item xs={12} sm={6} md={3} key={index}>
      <Card 
        sx={{
          p: 3,
          height: "130px",
          borderRadius: "16px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <img
              src={item.icon}
              alt={item.title}
              style={{ width: 25, height: 24 }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                color: item.color,
              }}
            >
              {item.title}
            </Typography>
          </Box>

          {/* ✅ Keep date on one line */}
          <Typography
            sx={{
              fontSize: "14px",
              color: "#808080",
              fontWeight: 500,
              whiteSpace: "nowrap", // <-- Prevents wrapping
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "90px", // keeps layout clean
              textAlign: "right",
            }}
          >
            {item.date}
          </Typography>
        </Stack>

        {/* Value + Arrow */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          // sx={{ mt: 2 }}
        >
          <Typography
            sx={{
              fontSize: "30px",
              fontWeight: 800,
              color: "#1E1E1E",
              // lineHeight: 1.1,
            }}
          >
            {item.value}
          </Typography>
          <img
            src={item.arrow}
            alt="trend"
          />
        </Stack>
      </Card>
    </Grid>
  ))}
</Grid>

        {/* Revenue Overview & Total Bookings */}
        <Grid container spacing={2}>
          {/* Revenue Overview */}
       
<Grid item xs={12} md={8}>
  <Card sx={{ p: 2 }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      {/* Left side: title */}
      <Box>
        <Typography fontWeight={700}>Revenue Overview</Typography>
        <Typography color="text.secondary">
          Overview of 12 months
        </Typography>
      </Box>

      {/* ✅ Right side: legend items side-by-side */}
      <Stack direction="row" spacing={2} alignItems="center">
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: "#F97316",
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Last Year
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: "#3B82F6",
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Current Year
          </Typography>
        </Stack>
      </Stack>
    </Stack>

    {/* Chart with formatted Y-axis */}
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data}>
       <XAxis
      dataKey="month"
      tick={{
        fill: "#1E1E1E", // dark text color
        fontWeight: 700, // bold
        fontSize: 13, // optional for readability
      }}
    />
       <YAxis
  tickFormatter={(value) => `${value / 1000}k`} // 6000 → 6k
  domain={[0, 24000]} 
  tick={{
    fill: "#1E1E1E",  
    fontWeight: 700,   
    fontSize: 13,     
  }}
/>

        <Tooltip
          formatter={(value, name) => [`₹${value}`, name]}
          cursor={{ fill: "rgba(0,0,0,0.05)" }}
        />
        {/* Legend removed */}
        <Bar dataKey="lastYear" fill="#F97316" radius={[5, 5, 0, 0]} />
        <Bar dataKey="currentYear" fill="#3B82F6" radius={[5, 5, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </Card>
</Grid>
          {/* Total Bookings */}
          <Grid item xs={12} md={4}>
  <Card sx={{ p: 2, height: 375 }}>
    <Typography fontWeight={700}>Total Bookings (This Week)</Typography>

    {/* Pie Chart */}
    <ResponsiveContainer width="100%" height={150}>
      <PieChart>
        <Tooltip formatter={(value, name) => `${name}: ${value} Bookings`} />
        <Pie
          data={pieData}
          startAngle={180}
          endAngle={0}
          innerRadius={60}
          outerRadius={100}
          dataKey="value"
          cx="50%"
          cy="100%"
        >
          {pieData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>

    {/* Total bookings number */}
    <Typography
      variant="h4"
      align="center"
      sx={{ mt: -6, fontWeight: 700 }}
    >
      {totalBookings}
    </Typography>
    <Typography align="center" color="text.secondary">
      Bookings
    </Typography>

    <Divider sx={{ my: 1 }} />

    {/* ✅ Updated legend with color circles */}
    <Stack direction="row" justifyContent="space-around">
      {pieData.map((item) => (
        <Stack key={item.name} alignItems="center">
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: item.color,
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: "#1E1E1E", fontWeight: 600 }}
            >
              {item.name}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {item.value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  </Card>
</Grid>

        </Grid>
      </Box>
    </AuthorizedLayout>
  );
};

export default Dashboard;
